import { Chip, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import {
  hasMediaAgentAuthIssue,
  resolveMediaAgentAutomationCaption,
  type MediaAgentAutomationSeverity,
} from '@/helpers/media-agent-automation.helper';
import type { MediaAgentPoolMemberDto } from '@/types/dtos/media-agent/media-agent-pool.dto';

const CAPTION_COLOR: Record<MediaAgentAutomationSeverity, string> = {
  info: 'text.secondary',
  success: 'success.main',
  warning: 'warning.main',
  error: 'error.main',
};

/**
 * Auth-health chip plus the background-heal caption for a pool row. Renders nothing when the
 * backend has not sent `authHealth` / `lastAutomation` yet.
 */
export default function MediaAgentAuthStatusCell({ agent }: { agent: MediaAgentPoolMemberDto }) {
  const t = useTranslations();
  const authIssue = hasMediaAgentAuthIssue(agent);
  const automation = resolveMediaAgentAutomationCaption(t, agent.lastAutomation);

  if (!authIssue && !automation) {
    return null;
  }

  const authTooltipParts = [
    agent.validUntil
      ? t(
          'admin.mediaAgentAdmin.auth.tokenValidUntil',
          { when: new Date(agent.validUntil).toLocaleString() },
          `Token valid until ${new Date(agent.validUntil).toLocaleString()}`,
        )
      : null,
    agent.lastAuthenticatedAt
      ? t(
          'admin.mediaAgentAdmin.auth.lastAuthenticated',
          { when: new Date(agent.lastAuthenticatedAt).toLocaleString() },
          `Last authenticated ${new Date(agent.lastAuthenticatedAt).toLocaleString()}`,
        )
      : t('admin.mediaAgentAdmin.auth.neverAuthenticated', undefined, 'Never authenticated'),
  ].filter((part): part is string => Boolean(part));

  return (
    <>
      {authIssue && (
        <Tooltip
          title={[
            t(
              'admin.mediaAgentAdmin.auth.jwtStaleHint',
              undefined,
              'Shell is up but its player token is rejected — reissue the token (Azure) or re-pair the device.',
            ),
            ...authTooltipParts,
          ].join(' · ')}
        >
          <Chip
            size="small"
            label={t('admin.mediaAgentAdmin.auth.jwtStale', undefined, 'JWT stale')}
            color="warning"
            variant="outlined"
          />
        </Tooltip>
      )}
      {automation && (
        <Tooltip title={automation.detail ?? ''}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {automation.inFlight && <CircularProgress size={10} />}
            <Typography variant="caption" color={CAPTION_COLOR[automation.severity]}>
              {automation.text}
            </Typography>
          </Stack>
        </Tooltip>
      )}
    </>
  );
}
